import { LitElement, html, css } from "lit";

export class RDEditorGuides extends LitElement {
  static properties = {
    width: { type: Number },
    height: { type: Number },
    showGrid: { type: Boolean },
    showRuler: { type: Boolean },
    showDevices: { type: Boolean },
    showGuides: { type: Boolean },
    showBreakpoints: { type: Boolean },
    cursorX: { type: Number },
    cursorY: { type: Number },
    unit: { type: String },
    step: { type: Number },
    contentWidth: { type: Number },
    zoom: { type: Number },
    currentDevice: { type: Object },
    selectedDevice: { type: String },
    selectedDeviceWidth: { type: Number },
  };

  static styles = css`
    :host {
      display: block;
      position: relative;
      width: 100%;
      height: calc(100% - var(--horizotal-element-tree-height));
      background-color: transparent;
      --ruler-thickness: 20px;
      --device-bar-height: 24px;
    }

    /* Layout Grid for the Component Structure */
    .rd-layout {
      display: grid;
      grid-template-rows: auto 1fr;
      grid-template-columns: auto 1fr;
      width: 100%;
      height: 100%;
      overflow: hidden;
    }

    /* Top Ruler Area */
    .rd-ruler-top-container {
      grid-column: 2;
      grid-row: 1;
      position: relative;
      height: calc(var(--device-bar-height) + var(--ruler-thickness));
      z-index: 1;
    }

    /* Left Ruler Area */
    .rd-ruler-left-container {
      grid-column: 1;
      grid-row: 2;
      position: relative;
      width: var(--ruler-thickness);
      z-index: 1;
    }

    /* Corner (Top-Left) */
    .rd-corner {
      grid-column: 1;
      grid-row: 1;
      background: transparent;
      z-index: 1;
    }

    /* Main Content Stage */
    .rd-stage {
      grid-column: 2;
      grid-row: 2;
      position: relative;
      overflow: hidden;
      background-color: var(--stage-bg, transparent);
      display: flex;
      justify-content: center;
      align-items: flex-start;
    }

    /* The Resizable Wrapper inside Stage */
    .rd-content-wrapper {
      position: relative;
      width: 800px;
      min-width: 320px;
      height: 100%;
      background: white;
      box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
      flex-shrink: 0;
      overflow: visible;
      /* transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1); */
    }

    .rd-content-wrapper.no-transition {
      transition: none;
    }

    /* Zoom container that gets the scale transform */
    .rd-zoom-container {
      transform-origin: 0px 0px;
      position: relative;
      width: var(--actual-width);
      height: var(--actual-height);
      transform: scale(var(--zoom-scale));
      /* transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1); */
    }

    .rd-zoom-container.no-transition {
      transition: none;
    }

    /* Resize Handle */
    .rd-resize-handle {
      position: absolute;
      top: 0;
      right: -16px;
      width: 16px;
      height: 100%;
      cursor: col-resize;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 30;
      transition: background 0.2s;
    }
    .rd-resize-handle::after {
      content: "";
      height: 24px;
      width: 2px;
      background-color: #888;
      border-radius: 1px;
    }
    .rd-resize-handle:hover {
      background-color: rgba(0, 123, 255, 0.1);
    }

    /* Canvas Overlay */
    canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 2; /* Above everything */
    }

    /* Interaction Layers */
    .interaction-layer {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 60;
      pointer-events: auto;
    }

    /* Resize Overlay - covers iframe during resize */
    .rd-resize-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 50;
      display: none;
      pointer-events: auto;
      background: transparent;
    }

    .rd-resize-overlay.active {
      display: block;
    }
  `;

  constructor() {
    super();
    this.width = 0;
    this.height = 0;
    this.showGrid = false;
    this.showRuler = true;
    this.showDevices = true;
    this.showGuides = false;
    this.showBreakpoints = true;
    this.cursorX = -100;
    this.cursorY = -100;
    this.unit = "px";
    this.step = 20;
    this.contentWidth = 1024;
    this.zoom = 1;
    this.currentDevice = null;

    // Constants
    this.rulerSize = 20;
    this.deviceBarHeight = 24;

    // Get devices from DeviceManager
    const deviceManager = window["DeviceManager"];
    const devices = deviceManager ? deviceManager.getDevices() : [];
    // Sort by width (smallest first for center positioning)
    this.breakpoints = devices.sort((a, b) => a.width - b.width);

    this._boundIframeMouseMove = this.handleIframeMouseMove.bind(this);
    this._boundIframeLoad = this.handleIframeLoad.bind(this);
    this._boundResizeMove = this.handleResizeMove.bind(this);
    this._boundResizeEnd = this.handleResizeEnd.bind(this);
    this._boundDeviceManagerUpdate = this.handleDeviceManagerUpdate.bind(this);
    this._boundScaleChanged = this.handleScaleChanged.bind(this);

    // Flag to enable auto-zoom only for device clicks, not manual resizing
    this._autoZoom = false;
    this._transitionAnimationId = null;
    this._hoveredUsedBreakpoint = null; // Track which used breakpoint is hovered
  }
  get usedBreakpoints() {
    return StylesheetManager.getElementBreakpoints();
  }
  calculateZoom() {
    // If zoom was manually set, use that value
    if (this._manualZoom !== undefined && this._manualZoom !== null) {
      return this._manualZoom;
    }

    // Only apply auto-zoom when explicitly enabled (device clicks)
    if (!this._autoZoom) {
      return 1;
    }

    const stage = this.renderRoot.querySelector(".rd-content-wrapper");
    if (!stage) return 1;

    const stageRect = stage.getBoundingClientRect();
    const padding = 0;// 80; // 40px on each side
    const stageWidth = stageRect.width - padding;

    // Use intended width if content is constrained, otherwise use actual content width
    const targetWidth = this._intendedWidth || this.contentWidth;

    // If content fits within stage, no zoom (100%)
    if (targetWidth <= stageWidth) {
      return 1;
    }

    // Content exceeds stage: zoom so it fills 100% of stage width
    return stageWidth / targetWidth;
  }

  get c() {
    // Read colors from CSS variables defined in _variables.scss
    const root = document.documentElement;
    if (!root) {
      // Fallback colors if document not ready
      return {
        rulerBg: "#f8fafc",
        rulerText: "#64748b",
        rulerTick: "#cbd5e1",
        borderColor: "#e2e8f0",
        gridLine: "rgba(0, 0, 0, 0.08)",
        gridMajor: "rgba(0, 0, 0, 0.15)",
        guideColor: "#007bff",
        deviceBarBg: "#ffffff",
        rangeBg: "#e2e8f0",
        rangeHover: "#cbd5e1",
        rangeBorder: "#cbd5e1",
        resizeHandleBg: "#f1f5f9",
        bgStage: "#f3f4f6",
      };
    }

    const getVar = (name) => getComputedStyle(root).getPropertyValue(name).trim();

    return {
      rulerBg: getVar("--ruler-bg") || "#f8fafc",
      rulerText: getVar("--ruler-text") || "#64748b",
      rulerTick: getVar("--ruler-tick-color") || "#cbd5e1",
      borderColor: getVar("--border-color") || "#e2e8f0",
      gridLine: getVar("--grid-line") || "rgba(0, 0, 0, 0.08)",
      gridMajor: getVar("--grid-major") || "rgba(0, 0, 0, 0.15)",
      guideColor: getVar("--guide-color") || "#007bff",
      deviceBarBg: getVar("--device-bar-bg") || "#ffffff",
      rangeBg: getVar("--range-bg") || "#e2e8f0",
      rangeHover: getVar("--range-hover") || "#cbd5e1",
      rangeBorder: getVar("--range-border") || "#cbd5e1",
      resizeHandleBg: getVar("--bg-panel") || "#f1f5f9",
      bgStage: getVar("--bg-app") || "#f3f4f6",
    };
  }

  firstUpdated() {
    this.canvas = this.renderRoot.getElementById("guides-canvas");
    if (this.canvas) this.ctx = this.canvas.getContext("2d");

    // Set initial intended width to the initial contentWidth
    // This ensures browser resize remembers the starting point
    this._intendedWidth = this.contentWidth;

    // Observe size of the host
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const oldWidth = this.width;
        this.width = entry.contentRect.width;
        this.height = entry.contentRect.height;

        if (oldWidth > 0) {
          const stage = this.renderRoot.querySelector(".rd-stage");
          if (stage) {
            const oldCanvasWidth = (stage.getBoundingClientRect().width / this.width) * oldWidth;
            const newCanvasWidth = stage.getBoundingClientRect().width;
            const wasFullWidth = this.contentWidth < 0 || Math.abs(this.contentWidth - oldCanvasWidth) < 5;

            // Detect if stage is shrinking
            const stageShrinking = newCanvasWidth < oldCanvasWidth;
            const stageGrowing = newCanvasWidth > oldCanvasWidth;

            const minWidth = 320; // Minimum device width

            // Priority 1: We have an intended width - restore it if stage can accommodate
            if (this._intendedWidth) {
              if (newCanvasWidth >= this._intendedWidth) {
                // Stage is big enough, restore to intended width and clear
                this.contentWidth = this._intendedWidth;
                this._intendedWidth = null;
              } else if (stageShrinking) {
                // Stage shrinking: clamp to stage width but don't go below minimum
                this.contentWidth = Math.max(minWidth, newCanvasWidth);
              } else if (stageGrowing) {
                // Stage growing but not enough yet: gradually grow content with stage
                this.contentWidth = Math.min(this._intendedWidth, Math.max(minWidth, newCanvasWidth));
              }
            }
            // Priority 2: Stage is shrinking and content is not at full width by choice
            else if (stageShrinking && !wasFullWidth) {
              // Save the current width as intended before it gets clamped
              this._intendedWidth = this.contentWidth;
              this.contentWidth = Math.max(minWidth, Math.min(this.contentWidth, newCanvasWidth));
            }
            // Priority 3: Stage shrunk below content width (content was bigger)
            else if (this.contentWidth > newCanvasWidth) {
              // Stage shrunk, clamp to new stage width but remember intended width
              this._intendedWidth = this.contentWidth;
              this.contentWidth = Math.max(minWidth, newCanvasWidth);
            }
            // Priority 4: Canvas was at full width with no intended width, follow stage
            else if (wasFullWidth && !this._intendedWidth) {
              // Canvas was at full width by choice, update to new stage width
              this.contentWidth = Math.max(minWidth, newCanvasWidth);
            }
          }

          // Check if new width matches any device
          this.matchDeviceByWidth();
        }

        this.requestUpdate();
      }
    });
    this.resizeObserver.observe(this);

    // Add mouse listeners to stage
    const stage = this.renderRoot.querySelector(".rd-stage");
    if (stage) {
      stage.addEventListener("mousemove", (e) => this.handleHostMouseMove(e));
      stage.addEventListener("mouseleave", () => this.handleMouseLeave());
    }

    // Add mouse listeners to top interaction area (for device bar hover)
    const topLayer = this.renderRoot.getElementById("interaction-top");
    if (topLayer) {
      topLayer.addEventListener("mousemove", (e) => this.handleHostMouseMove(e));
      topLayer.addEventListener("click", (e) => this.handleTopClick(e));
      topLayer.addEventListener("mouseleave", () => this.handleMouseLeave());
    }

    // Listen for theme changes
    this.handleThemeChange = () => {
      this.requestUpdate();
    };
    window.addEventListener(canvasEventsLabels.themeChanged, this.handleThemeChange);

    // Subscribe to DeviceManager updates
    if (window["DeviceManager"]) {
      this._deviceManagerUnsubscribe = window["DeviceManager"].on("updated", this._boundDeviceManagerUpdate);
      this._scaleChangedUnsubscribe = window["DeviceManager"].on("scale:changed", this._boundScaleChanged);
    }
  }

  handleScaleChanged(scale) {
    if (!scale || typeof scale.scale !== "number") return;
    this.setZoom(scale?.scale, true); // skipSync=true to prevent feedback loop
  }

  handleDeviceManagerUpdate(data) {
    // Don't sync if we're the source of the change (avoid feedback loops)
    if (this._isResizing) return;

    // Sync breakpoints when devices list changes
    if (data.type && data.type.includes("device")) {
      const deviceManager = window["DeviceManager"];
      if (deviceManager) {
        this.breakpoints = deviceManager.getDevices().sort((a, b) => a.width - b.width);
      }
    }

    // Sync state from DeviceManager
    if (window["DeviceManager"]) {
      const state = window["DeviceManager"].getState();

      // Sync width (the actual canvas size) - only if it's actually different
      if (typeof state.width === "number") {
        const currentWidth = this._intendedWidth || this.contentWidth;
        const widthChanged = Math.abs(state.width - currentWidth) > 1; // Tolerance for floating point

        if (widthChanged) {
          this._autoZoom = true;
          this._manualZoom = null;
          const stage = this.renderRoot?.querySelector(".rd-stage");
          const stageWidth = stage?.getBoundingClientRect().width || 0; // If desired width exceeds stage, save as intended and clamp to stage
          if (state.width > stageWidth && stageWidth > 0) {
            this._intendedWidth = state.width;
            this.contentWidth = stageWidth;
          } else {
            this.contentWidth = state.width;
            this._intendedWidth = null;
            //this._autoZoom = false;
          }

          this.requestUpdate();
          this.startTransitionAnimation();

          // Sync calculated zoom back to DeviceManager after width change
          requestAnimationFrame(() => {
            const calculatedZoom = this.calculateZoom();
            if (window["DeviceManager"]) {
              window["DeviceManager"].setScale(calculatedZoom);
            }
          });
        }
      }

      // Sync current device (for the device bar display)
      const deviceName = state.activeDevice;
      if (deviceName) {
        const device = this.breakpoints.find((d) => d.name?.toLowerCase() === deviceName);
        if (device && (!this.currentDevice || this.currentDevice.width !== device.width)) {
          this.currentDevice = device;
          this.requestUpdate();
        }
      } else if (this.currentDevice) {
        this.currentDevice = null;
        this.requestUpdate();
      }

      // Sync zoom/scale from DeviceManager
      // const scaleChanged = Math.abs(state.scale - this.zoom) > 0.01; // Tolerance for floating point
      // if (scaleChanged) {
      // //  this._manualZoom = state.scale;
      //  // this._autoZoom = false;
      //   this.requestUpdate();
      //   this.startTransitionAnimation();
      // }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.handleThemeChange) {
      window.removeEventListener(canvasEventsLabels.themeChanged, this.handleThemeChange);
    }
    // Unsubscribe from DeviceManager
    if (this._deviceManagerUnsubscribe) {
      this._deviceManagerUnsubscribe();
    }
    if (this._scaleChangedUnsubscribe) {
      this._scaleChangedUnsubscribe();
    }
    // Stop any ongoing transition animation
    this.stopTransitionAnimation();
  }

  // --- OVERLAY HELPERS ---
  showOverlay() {
    const overlay = this.shadowRoot.querySelector(".rd-resize-overlay");
    if (overlay) overlay.classList.add("active");
  }

  hideOverlay() {
    const overlay = this.shadowRoot.querySelector(".rd-resize-overlay");
    if (overlay) overlay.classList.remove("active");
  }

  // --- RESIZE HANDLE LOGIC ---
  startResize(e) {
    e.preventDefault();
    this._isResizing = true;
    this._startX = e.clientX;

    // Show overlay to prevent iframe interaction
    this.showOverlay();

    // Stop any running transition animation
    this.stopTransitionAnimation();

    // Disable transitions during manual resize for smooth dragging
    const wrapper = this.shadowRoot.querySelector(".rd-content-wrapper");
    const zoomContainer = this.shadowRoot.querySelector(".rd-zoom-container");
    if (wrapper) wrapper.classList.add("no-transition");
    if (zoomContainer) zoomContainer.classList.add("no-transition");

    // Disable auto-zoom when user manually resizes
    this._autoZoom = false;
    this._manualZoom = null; // Clear manual zoom on resize
    // Don't clear currentDevice here - let matchDeviceByWidth handle it during/after resize

    // If starting from full width (-1), calculate the actual canvas width once
    const canvasWidth = this.shadowRoot.querySelector(".rd-stage")?.getBoundingClientRect().width || 0;
    this._startWidth = this.contentWidth === -1 ? canvasWidth : this.contentWidth;

    window.addEventListener("mousemove", this._boundResizeMove);
    window.addEventListener("mouseup", this._boundResizeEnd);

    // Dispatch start event
    this.dispatchEvent(new CustomEvent("canvas-resize-start", { detail: { width: this.contentWidth } }));
  }
  addShield = () => {
    return;
    const canvas = document.getElementById("canvas");
    if (canvas && canvas.classList && !canvas.classList.contains("disabled")) canvas.classList.add("disabled");
  };
  removeShield = () => {
    return;
    const canvas = document.getElementById("canvas");
    if (canvas) {
      canvas.classList.remove("disabled");
      canvas.contentWindow.scrollBy(5, 0);
    }
  };

  handleResizeMove(e) {
    if (!this._isResizing) return;

    const deltaX = e.clientX - this._startX;
    // When zoomed, adjust delta to account for scale
    const actualDelta = deltaX / this.zoom;
    const canvasWidth = this.shadowRoot.querySelector(".rd-stage")?.getBoundingClientRect().width || 0;

    // Calculate new width, ensuring minimum of 320px
    const newWidth = Math.max(320, this._startWidth + actualDelta * 2);

    // Always track intended width (what user wants)
    this._intendedWidth = newWidth;

    // Clamp to canvas width - but remember the intended width
    this.contentWidth = Math.min(newWidth, canvasWidth);

    // Throttle updates during resize using RAF
    if (!this._resizeRafPending) {
      this._resizeRafPending = true;
      requestAnimationFrame(() => {
        this._resizeRafPending = false;
        this.requestUpdate();
      });
    }

    this.dispatchEvent(new CustomEvent("canvas-resize", { detail: { width: this.contentWidth, zoom: this.zoom } }));
  }

  handleResizeEnd() {
    this._isResizing = false;
    this._resizeRafPending = false; // Clear RAF flag

    // Hide overlay to restore iframe interaction
    this.hideOverlay();

    // Re-enable transitions after resize
    const wrapper = this.shadowRoot.querySelector(".rd-content-wrapper");
    const zoomContainer = this.shadowRoot.querySelector(".rd-zoom-container");
    if (wrapper) wrapper.classList.remove("no-transition");
    if (zoomContainer) zoomContainer.classList.remove("no-transition");

    // Only clear intended width if user actively dragged to full width
    // Check if the intended width (what user wanted) is at or near canvas width
    const canvasWidth = this.shadowRoot.querySelector(".rd-stage")?.getBoundingClientRect().width || 0;
    if (this._intendedWidth && Math.abs(this._intendedWidth - canvasWidth) < 5) {
      this._intendedWidth = null;
    }

    // Check if current width matches any device
    this.matchDeviceByWidth();

    // Update DeviceManager with new width
    if (window["DeviceManager"]) {
      window["DeviceManager"].selectedWidth = this.contentWidth;
    }

    window.removeEventListener("mousemove", this._boundResizeMove);
    window.removeEventListener("mouseup", this._boundResizeEnd);

    // Final update after resize
    this.requestUpdate();

    // Sync calculated zoom to DeviceManager after resize
    requestAnimationFrame(() => {
      const calculatedZoom = this.calculateZoom();
      if (window["DeviceManager"]) {
        window["DeviceManager"].setScale(calculatedZoom);
      }
    });

    this.dispatchEvent(new CustomEvent("canvas-resize-end", { detail: { width: this.contentWidth, zoom: this.zoom } }));
  }

  // --- IFRAME LOGIC (Same as before) ---
  handleSlotChange(e) {
    const nodes = e.target.assignedElements({ flatten: true });
    const iframe = nodes.find((n) => n.tagName === "IFRAME");
    if (iframe) {
      this._iframe = iframe; // Store reference
      iframe.removeEventListener("load", this._boundIframeLoad);
      iframe.addEventListener("load", this._boundIframeLoad);
      if (iframe.contentDocument && (iframe.contentDocument.readyState === "complete" || iframe.contentDocument.readyState === "interactive")) {
        this.setupIframeListeners(iframe);
      }
      // Set initial dimensions and transform
      this.updateIframeAttributes();
    }
  }

  updateIframeAttributes() {
    if (!this._iframe) return;

    const stage = this.renderRoot?.querySelector(".rd-stage");
    const stageHeight = stage?.getBoundingClientRect().height || 0;
    const actualWidth = this._intendedWidth || this.contentWidth;

    // When zoomed, the iframe needs to be larger so that when scaled it fills the view
    // Divide by zoom to get the unscaled dimensions
    const iframeHeight = this.zoom < 1 ? stageHeight / this.zoom : stageHeight;

    // Set width/height attributes directly on iframe
    this._iframe.setAttribute("width", actualWidth);
    this._iframe.setAttribute("height", Math.round(iframeHeight));
  }

  handleIframeLoad(e) {
    this.setupIframeListeners(e.target);
  }

  setupIframeListeners(iframe) {
    try {
      const iDoc = iframe.contentDocument;
      if (iDoc) {
        iDoc.removeEventListener("mousemove", this._boundIframeMouseMove);
        iDoc.addEventListener("mousemove", this._boundIframeMouseMove);
      }
    } catch (e) { }
  }

  handleIframeMouseMove(ev) {
    // We need to calculate where the iframe is relative to the Host Component (0,0)
    // The iframe is inside .rd-content-wrapper which is centered in .rd-stage

    const wrapper = this.renderRoot.querySelector(".rd-content-wrapper");
    if (!wrapper) return;
    const wrapperRect = wrapper.getBoundingClientRect();
    const hostRect = this.getBoundingClientRect();

    // Offset of wrapper relative to host
    const offsetX = wrapperRect.left - hostRect.left;
    const offsetY = wrapperRect.top - hostRect.top;

    // When zoomed, the mouse coordinates from iframe need to be scaled
    // ev.clientX/Y are in iframe's unscaled coordinate space
    const scaledMouseX = ev.clientX * this.zoom;
    const scaledMouseY = ev.clientY * this.zoom;

    this.cursorX = scaledMouseX + offsetX;
    this.cursorY = scaledMouseY + offsetY;
    this.requestUpdate();

    this.dispatchCoords(scaledMouseX, scaledMouseY);
  }

  handleHostMouseMove(e) {
    const rect = this.getBoundingClientRect();
    this.cursorX = e.clientX - rect.left;
    this.cursorY = e.clientY - rect.top;
    this.requestUpdate();

    // Calculate coords relative to content wrapper
    const wrapper = this.renderRoot.querySelector(".rd-content-wrapper");
    if (wrapper) {
      const wRect = wrapper.getBoundingClientRect();
      // If mouse is inside wrapper visually
      const localX = e.clientX - wRect.left;
      const localY = e.clientY - wRect.top;
      this.dispatchCoords(localX, localY);
    }
  }

  dispatchCoords(x, y) {
    this.dispatchEvent(
      new CustomEvent("guides-mousemove", {
        detail: { x: Math.round(x), y: Math.round(y) },
      }),
    );
  }

  handleMouseLeave() {
    this.cursorX = -100;
    this.cursorY = -100;
    this.requestUpdate();
  }

  updated() {
    this.zoom = this.calculateZoom();
    this.updateIframeAttributes(); // Update iframe dimensions and transform
    this.draw();
  }

  // --- DRAWING ---
  draw() {
    if (!this.canvas || !this.ctx) return;
    const c = this.c; // Get current theme colors
    const dpr = window.devicePixelRatio || 1;

    this.canvas.width = this.width * dpr;
    this.canvas.height = this.height * dpr;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.scale(dpr, dpr);
    this.ctx.clearRect(0, 0, this.width, this.height);

    // We draw rulers and grid relative to the "Content Wrapper" position
    const wrapper = this.renderRoot.querySelector(".rd-content-wrapper");
    if (!wrapper) return;

    // Calculate geometry
    const wrapperRect = wrapper.getBoundingClientRect();
    const hostRect = this.getBoundingClientRect();

    // contentX/Y relative to component 0,0
    const contentX = wrapperRect.left - hostRect.left;
    const contentY = wrapperRect.top - hostRect.top;
    // Use actual visual width during transitions to avoid ruler glitches
    const contentW = wrapperRect.width;
    const contentH = wrapperRect.height; // Height is usually 100% of stage

    const deviceH = this.showDevices ? this.deviceBarHeight : 0;
    const rulerH = this.showRuler ? this.rulerSize : 0;
    const rulerW = this.showRuler ? this.rulerSize : 0;

    // 1. Draw Fixed UI backgrounds (Rulers/Bars)
    if (this.showDevices || this.showRuler) {
      this.ctx.fillStyle = c.rulerBg;
      this.ctx.fillRect(0, 0, this.width, deviceH + rulerH); // Top Bar Area
      this.ctx.fillRect(0, 0, rulerW, this.height); // Left Bar Area
    }

    // 2. Draw device bar (if enabled)
    if (this.showDevices) {
      this.drawDeviceBar(contentX, deviceH, c);
    }

    // 3. Draw rulers (if enabled)
    if (this.showRuler) {
      this.drawRulers(contentX, contentY, deviceH, c);
    }

    // 4. Draw Grid (Canvas-wide, starting after rulers)
    if (this.showGrid) {
      this.drawGrid(rulerW, deviceH + rulerH, this.width - rulerW, this.height - deviceH - rulerH, contentX, contentY, contentW, contentH, c);
    }

    // 5. Guides (on top of everything)
    if (this.showGuides) {
      this.drawMouseGuides(c, deviceH, rulerH);
    }
  }

  drawGrid(x, y, w, h, contentX, contentY, contentW, contentH, c) {
    const ctx = this.ctx;
    ctx.save();

    // Use a medium gray that works on both light and dark backgrounds
    ctx.strokeStyle = "rgba(128, 128, 128, 0.25)";
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 4]);

    ctx.beginPath();

    // Calculate grid starting points aligned with content coordinate system
    // Content starts at contentX, and we want grid at 0, step, step*2, etc. in content coordinates
    const startX = Math.floor(-contentX / this.step) * this.step;
    const endX = Math.ceil((w - contentX + x) / this.step) * this.step;

    const startY = Math.floor(-contentY / this.step) * this.step;
    const endY = Math.ceil((h - contentY + y) / this.step) * this.step;

    // Draw vertical lines (aligned to content's X coordinates)
    for (let i = startX; i <= endX; i += this.step) {
      const screenX = contentX + i;
      if (screenX >= x && screenX <= x + w) {
        ctx.moveTo(screenX + 0.5, y);
        ctx.lineTo(screenX + 0.5, y + h);
      }
    }

    // Draw horizontal lines (aligned to content's Y coordinates)
    for (let j = startY; j <= endY; j += this.step) {
      const screenY = contentY + j;
      if (screenY >= y && screenY <= y + h) {
        ctx.moveTo(x, screenY + 0.5);
        ctx.lineTo(x + w, screenY + 0.5);
      }
    }

    ctx.stroke();
    ctx.restore();
  }

  drawRulers(contentX, contentY, startY, c) {
    const ctx = this.ctx;
    const h = this.rulerSize;

    // Draw ruler borders
    ctx.strokeStyle = c.borderColor;
    ctx.lineWidth = 1;

    // Top ruler border
    ctx.beginPath();
    ctx.moveTo(0, startY + h - 0.5);
    ctx.lineTo(this.width, startY + h - 0.5);
    ctx.stroke();

    // Left ruler border
    ctx.beginPath();
    ctx.moveTo(h - 0.5, startY);
    ctx.lineTo(h - 0.5, this.height);
    ctx.stroke();

    // Setup text rendering
    ctx.fillStyle = c.rulerText;
    ctx.font = "10px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Get wrapper visual width to calculate zoom ratio
    const wrapper = this.renderRoot?.querySelector(".rd-content-wrapper");
    const wrapperRect = wrapper?.getBoundingClientRect();
    const wrapperVisualWidth = wrapperRect?.width || this.contentWidth;

    // Use intended width when zoomed, otherwise use contentWidth
    const logicalWidth = this.zoom < 1 && this._intendedWidth ? this._intendedWidth : this.contentWidth;

    // Calculate scale: visual pixels per logical pixel
    const scale = wrapperVisualWidth / logicalWidth;

    // X-Axis ruler (horizontal) - continue beyond logical width if space available
    // Calculate how far the ruler should extend on both sides
    const minLogicalValue = Math.floor((h - contentX) / scale / this.step) * this.step;
    const maxVisibleX = this.width - h; // Max visible screen position
    const maxLogicalValue = Math.ceil((maxVisibleX - contentX) / scale / this.step) * this.step;

    for (let v = minLogicalValue; v <= maxLogicalValue; v += this.step) {
      const screenX = contentX + v * scale;
      if (screenX < h) continue; // Don't draw over left ruler area
      if (screenX > this.width) break; // Stop if beyond visible area

      // Draw tick mark
      ctx.beginPath();
      ctx.moveTo(screenX + 0.5, startY + h - 5);
      ctx.lineTo(screenX + 0.5, startY + h);
      ctx.strokeStyle = c.rulerTick;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Draw label every 4 steps (including 0)
      if (v % (this.step * 4) === 0) {
        ctx.fillStyle = c.rulerText;
        ctx.fillText(String(v), screenX, startY + h / 2);
      }
    }

    // Y-Axis ruler (vertical)
    const wrapperVisualHeight = wrapperRect?.height || this.height;
    const logicalHeight = wrapperVisualHeight / this.zoom; // Calculate logical height based on zoom
    const scaleY = wrapperVisualHeight / logicalHeight;

    // Calculate how far the vertical ruler should extend on both sides
    const minLogicalValueY = Math.floor((startY + h - contentY) / scaleY / this.step) * this.step;
    const maxLogicalValueY = Math.ceil((this.height - contentY) / scaleY / this.step) * this.step;

    for (let v = minLogicalValueY; v <= maxLogicalValueY; v += this.step) {
      const screenY = contentY + v * scaleY;
      if (screenY < startY + h) continue; // Don't draw over top area
      if (screenY > this.height) break; // Stop if beyond visible area

      // Draw tick mark
      ctx.beginPath();
      ctx.moveTo(h - 5, screenY + 0.5);
      ctx.lineTo(h, screenY + 0.5);
      ctx.strokeStyle = c.rulerTick;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Draw label every 4 steps (including 0)
      if (v % (this.step * 4) === 0) {
        ctx.save();
        ctx.fillStyle = c.rulerText;
        ctx.translate(h / 2, screenY);
        ctx.rotate(-Math.PI / 2);
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(v), 0, 0);
        ctx.restore();
      }
    }
  }

  /**
   * Generate colors dynamically for breakpoint indicators
   * Uses HSL color space to create evenly distributed, distinct colors
   * @param {number} count - Number of colors to generate
   * @returns {Array} Array of color objects with normal and hover variants
   */
  generateBreakpointColors(count) {
    const colors = [];

    for (let i = 0; i < count; i++) {
      // Distribute hues evenly across the color wheel (0-360 degrees)
      const hue = (i * 360 / count) % 360;

      // Use vibrant saturation and medium lightness for normal state
      const saturation = 70;
      const lightnessNormal = 55;
      const lightnessHover = 45; // Darker for hover

      colors.push({
        normal: `hsl(${hue}, ${saturation}%, ${lightnessNormal}%)`,
        hover: `hsl(${hue}, ${saturation}%, ${lightnessHover}%)`
      });
    }

    return colors;
  }

  drawDeviceBar(contentX, startY, c) {
    const ctx = this.ctx;
    const h = this.deviceBarHeight;
    const rulerW = this.showRuler ? this.rulerSize : 0;

    // Draw device bar background
    ctx.fillStyle = c.deviceBarBg;
    ctx.fillRect(0, 0, this.width, h);

    // Bottom border
    ctx.beginPath();
    ctx.moveTo(0, h);
    ctx.lineTo(this.width, h);
    ctx.strokeStyle = c.borderColor;
    ctx.stroke();

    // Center point is the middle of the content area (Chrome style)
    // Use actual visual width for accurate positioning during transitions
    const wrapper = this.renderRoot.querySelector(".rd-content-wrapper");
    const actualWidth = wrapper?.getBoundingClientRect().width || this.contentWidth;
    const centerX = contentX + actualWidth / 2;
    // Breakpoints are positioned symmetrically around the content center

    // Find which breakpoint is hovered (smallest one that contains cursor)
    let hoveredIndex = -1;
    let hoveredDevice = null;
    let hoveredUsedBreakpoint = null;
    // Only check hover if cursor is in valid range (not initialized to -100)
    if (this.cursorX >= 0 && this.cursorY >= 0 && this.cursorY <= h) {
      // First check if hovering over any used breakpoint indicator (priority) - only if breakpoints are shown
      if (this.showBreakpoints) {
        for (const usedWidth of this.usedBreakpoints) {
          // Calculate the start and end positions of the horizontal line
          const lineStartX = centerX - (actualWidth / 2); // Left edge of canvas (0px position)
          const lineEndX = centerX + (usedWidth - actualWidth / 2); // Breakpoint position

          // Check if cursor is within the horizontal line range and near the top of device bar
          const isInXRange = this.cursorX >= lineStartX && this.cursorX <= lineEndX;
          const isNearTop = this.cursorY <= 6; // Top 6px of device bar

          if (isInXRange && isNearTop) {
            hoveredUsedBreakpoint = usedWidth;
            break;
          }
        }
      }

      // If not hovering a used breakpoint indicator, check device ranges
      if (!hoveredUsedBreakpoint) {
        for (let i = 0; i < this.breakpoints.length; i++) {
          const bp = this.breakpoints[i];
          const halfWidth = bp.width / 2;
          const xStart = centerX - halfWidth;
          const xEnd = centerX + halfWidth;

          if (this.cursorX >= xStart && this.cursorX <= xEnd) {
            hoveredIndex = i;
            hoveredDevice = bp;
            break; // Stop at first (smallest) match
          }
        }
      }
    }

    // Store hovered used breakpoint for click handling
    this._hoveredUsedBreakpoint = hoveredUsedBreakpoint;

    // Draw active device highlight (if currentDevice is set and not hovering)
    if (this.currentDevice && hoveredIndex === -1) {
      const activeBp = this.currentDevice;
      const halfWidth = activeBp.width / 2;
      const xStart = centerX - halfWidth;
      const xEnd = centerX + halfWidth;
      ctx.fillStyle = "rgba(59, 130, 246, 0.15)"; // Blue tint for active device
      ctx.fillRect(xStart, 0, xEnd - xStart, h);

      // Show active device label
      ctx.fillStyle = "rgb(59, 130, 246)"; // Blue text
      ctx.font = "bold 11px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const zoomText = this._manualZoom && this._manualZoom !== 1 ? ` @ ${Math.round(this._manualZoom * 100)}%` : "";
      ctx.fillText(`${activeBp.label} (${activeBp.width}px${zoomText})`, centerX, h / 2);
    }

    // Draw hover highlight symmetrically (Chrome style) - takes priority over active
    if (hoveredIndex >= 0) {
      const hoveredBp = this.breakpoints[hoveredIndex];
      const halfWidth = hoveredBp.width / 2;
      const xStart = centerX - halfWidth;
      const xEnd = centerX + halfWidth;
      ctx.fillStyle = c.rangeHover;
      ctx.fillRect(xStart, 0, xEnd - xStart, h);

      // Show device label on hover
      ctx.fillStyle = c.rulerText;
      ctx.font = "11px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const isActive = this.currentDevice && this.currentDevice.width === hoveredDevice.width;
      const prefix = isActive ? "✓ " : "";
      ctx.fillText(`${prefix}${hoveredDevice.label} (${hoveredDevice.width}px)`, centerX, h / 2);
    }

    // Draw breakpoint markers symmetrically around center (no labels by default)
    this.breakpoints.forEach((bp, index) => {
      const halfWidth = bp.width / 2;
      const xLeft = centerX - halfWidth;
      const xRight = centerX + halfWidth;

      // Use accent color for active device markers
      const isActive = this.currentDevice && this.currentDevice.width === bp.width;
      const strokeColor = isActive ? "rgb(59, 130, 246)" : c.rangeBorder;
      const lineWidth = isActive ? 2 : 1;

      // Draw left separator line
      if (xLeft >= rulerW && xLeft < this.width) {
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        ctx.moveTo(xLeft + 0.5, 0);
        ctx.lineTo(xLeft + 0.5, h);
        ctx.stroke();
      }

      // Draw right separator line
      if (xRight >= rulerW && xRight < this.width) {
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        ctx.moveTo(xRight + 0.5, 0);
        ctx.lineTo(xRight + 0.5, h);
        ctx.stroke();
      }
    });

    // Draw used breakpoint indicators as horizontal lines (on top of everything - only if enabled)
    if (!this.showBreakpoints) return;

    // Sort breakpoints to draw segments between them
    const sortedBreakpoints = [...this.usedBreakpoints].sort((a, b) => a - b);

    // Generate colors dynamically based on number of breakpoints
    const breakpointColors = this.generateBreakpointColors(sortedBreakpoints.length);

    sortedBreakpoints.forEach((usedWidth, index) => {
      const lineY = 2; // Position near top of device bar

      // Calculate segment start and end
      const segmentStartWidth = index === 0 ? 0 : sortedBreakpoints[index - 1];
      const segmentStartX = centerX + (segmentStartWidth - actualWidth / 2);
      const segmentEndX = centerX + (usedWidth - actualWidth / 2);

      const isHovered = hoveredUsedBreakpoint === usedWidth;

      // Only draw if the segment is visible
      if (segmentEndX >= rulerW) {
        ctx.save();

        // Line styling with dynamically generated color for each breakpoint
        const colorPalette = breakpointColors[index];
        const lineThickness = isHovered ? 2 : 1;
        const lineColor = isHovered ? colorPalette.hover : colorPalette.normal;

        // When hovered, draw from 0 to end, otherwise just draw the segment
        const drawStartX = isHovered ? centerX - (actualWidth / 2) : segmentStartX;

        // Draw horizontal line segment with its own color
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = lineThickness;
        ctx.beginPath();
        ctx.moveTo(Math.max(drawStartX, rulerW), lineY);
        ctx.lineTo(segmentEndX, lineY);
        ctx.stroke();

        // Draw end cap/marker at breakpoint position
        ctx.fillStyle = lineColor;
        const capHeight = isHovered ? 6 : 4;
        ctx.fillRect(
          segmentEndX - 1,
          0,
          2,
          capHeight
        );

        ctx.restore();

        // Draw width label on hover at the end of the segment
        if (isHovered) {
          ctx.save();

          const label = `${usedWidth}px`;
          const textWidth = ctx.measureText(label).width;
          const padding = 6;
          const tooltipHeight = 18;
          const offsetX = 8;
          const offsetY = 8;
          const borderRadius = 4;

          const tooltipX = segmentEndX + offsetX;
          const tooltipY = offsetY;
          const tooltipWidth = textWidth + padding * 2;

          // Draw rounded rectangle background
          ctx.fillStyle = c.deviceBarBg;
          ctx.strokeStyle = c.borderColor;
          ctx.lineWidth = 1;

          // Save shadow state
          ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
          ctx.shadowBlur = 6;
          ctx.shadowOffsetY = 2;

          ctx.beginPath();
          ctx.moveTo(tooltipX + borderRadius, tooltipY);
          ctx.lineTo(tooltipX + tooltipWidth - borderRadius, tooltipY);
          ctx.arcTo(tooltipX + tooltipWidth, tooltipY, tooltipX + tooltipWidth, tooltipY + borderRadius, borderRadius);
          ctx.lineTo(tooltipX + tooltipWidth, tooltipY + tooltipHeight - borderRadius);
          ctx.arcTo(tooltipX + tooltipWidth, tooltipY + tooltipHeight, tooltipX + tooltipWidth - borderRadius, tooltipY + tooltipHeight, borderRadius);
          ctx.lineTo(tooltipX + borderRadius, tooltipY + tooltipHeight);
          ctx.arcTo(tooltipX, tooltipY + tooltipHeight, tooltipX, tooltipY + tooltipHeight - borderRadius, borderRadius);
          ctx.lineTo(tooltipX, tooltipY + borderRadius);
          ctx.arcTo(tooltipX, tooltipY, tooltipX + borderRadius, tooltipY, borderRadius);
          ctx.closePath();
          ctx.fill();

          // Reset shadow for border
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetY = 0;
          ctx.stroke();

          // Draw text
          ctx.fillStyle = c.rulerText;
          ctx.font = 'bold 10px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, tooltipX + tooltipWidth / 2, tooltipY + tooltipHeight / 2);

          ctx.restore();
        }
      }
    });
  }

  drawMouseGuides(c, deviceH, rulerH) {
    if (this.cursorX < 0) return;
    if (this.cursorY < 0) return;
    if (this.cursorY < deviceH + rulerH) return; // Don't draw if in top bar area
    const ctx = this.ctx;
    ctx.strokeStyle = c.guideColor;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);

    ctx.beginPath();
    ctx.moveTo(this.cursorX + 0.5, 0);
    ctx.lineTo(this.cursorX + 0.5, this.height);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(0, this.cursorY + 0.5);
    ctx.lineTo(this.width, this.cursorY + 0.5);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  /**
   * Start continuous rendering during transitions
   */
  startTransitionAnimation() {
    // Clear any existing animation
    this.stopTransitionAnimation();

    const duration = 350; // Slightly longer than CSS transition (300ms) to ensure completion
    const startTime = performance.now();
    let lastDrawTime = startTime;
    const drawInterval = 16; // Draw every 16ms (~60fps) instead of every frame

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;

      // Continue animating during transition
      if (elapsed < duration) {
        // Only draw if enough time has passed since last draw
        if (currentTime - lastDrawTime >= drawInterval) {
          this.draw(); // Redraw to sync with CSS transition
          lastDrawTime = currentTime;
        }
        this._transitionAnimationId = requestAnimationFrame(animate);
      } else {
        // Final draw after transition completes
        this.draw();
        this._transitionAnimationId = null;
      }
    };

    this._transitionAnimationId = requestAnimationFrame(animate);
  }

  /**
   * Stop continuous rendering
   */
  stopTransitionAnimation() {
    if (this._transitionAnimationId) {
      cancelAnimationFrame(this._transitionAnimationId);
      this._transitionAnimationId = null;
    }
  }

  // Click handling for devices
  handleTopClick(e) {
    if (!this.showDevices) return;

    const wrapper = this.renderRoot.querySelector(".rd-content-wrapper");
    const hostRect = this.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const contentX = wrapperRect.left - hostRect.left;
    const centerX = contentX + this.contentWidth / 2;
    const clickX = e.clientX - hostRect.left;

    // Check if clicked on a used breakpoint indicator (priority)
    if (this._hoveredUsedBreakpoint !== null) {
      // Dispatch event with the clicked used breakpoint
      this.dispatchEvent(
        new CustomEvent('used-breakpoint-click', {
          detail: { width: this._hoveredUsedBreakpoint },
          bubbles: true,
          composed: true
        })
      );

      // Try to find a device that matches this breakpoint
      const device = this.breakpoints.find((d) => d.width === this._hoveredUsedBreakpoint);
      if (device) {
        // Use setDevice for proper synchronization
        this.setDevice(device);
      } else {
        // No exact device match - create a temporary device-like width
        // Update DeviceManager with the new width
        if (window["DeviceManager"]) {
          window["DeviceManager"].selectedWidth = this._hoveredUsedBreakpoint;
        }

        // Clear current device since this is a custom width
        this.currentDevice = null;
        this._manualZoom = null;

        const stage = this.renderRoot.querySelector(".rd-stage");
        const stageWidth = stage?.getBoundingClientRect().width || 0;

        // If breakpoint width exceeds stage, save as intended and enable auto-zoom
        if (this._hoveredUsedBreakpoint > stageWidth && stageWidth > 0) {
          this._intendedWidth = this._hoveredUsedBreakpoint;
          this.contentWidth = stageWidth;
          this._autoZoom = true;
        } else {
          this.contentWidth = this._hoveredUsedBreakpoint;
          this._intendedWidth = null;
          this._autoZoom = false;
        }

        this.requestUpdate();
        this.startTransitionAnimation();

        // Sync calculated zoom back to DeviceManager
        requestAnimationFrame(() => {
          const calculatedZoom = this.calculateZoom();
          if (window["DeviceManager"]) {
            window["DeviceManager"].setScale(calculatedZoom);
          }
        });
      }
      return;
    }

    // Check which symmetric breakpoint was clicked (smallest first)
    for (const bp of this.breakpoints) {
      const halfWidth = bp.width / 2;
      const xStart = centerX - halfWidth;
      const xEnd = centerX + halfWidth;

      if (clickX >= xStart && clickX <= xEnd) {
        this.setDevice(bp);
        return;
      }
    }
  }

  /**
   * Set the current device programmatically
   * @param {Object|string} device - Device object {width, label} or device label string
   * @param {Object} options - Optional configuration {zoom: number, animate: boolean}
   */
  setDevice(device, options = {}) {
    let bp = device;

    // If device is a string (label), find the matching device
    if (typeof device === "string") {
      bp = this.breakpoints.find((d) => d.label.toLowerCase() === device.toLowerCase());
      if (!bp) {
        console.warn(`Device "${device}" not found`);
        return;
      }
    }

    // Validate device object
    if (!bp || typeof bp.width !== "number") {
      console.warn("Invalid device object", bp);
      return;
    }

    this.currentDevice = bp;
    this._manualZoom = null; // Clear manual zoom when setting device

    // Update DeviceManager
    if (window["DeviceManager"]) {
      const deviceName = bp.name || bp.label;
      if (deviceName && window["DeviceManager"].hasDevice(deviceName)) {
        window["DeviceManager"].selectDevice(deviceName);
      }
    }

    this.dispatchEvent(new CustomEvent("device-click", { detail: bp }));

    // Auto resize
    const stage = this.renderRoot.querySelector(".rd-stage");
    const stageWidth = stage?.getBoundingClientRect().width || 0;

    // If device width exceeds stage, save it as intended and enable auto-zoom
    if (bp.width > stageWidth && stageWidth > 0) {
      this._intendedWidth = bp.width;
      this.contentWidth = stageWidth;
      this._autoZoom = true; // Enable zoom for device clicks
    } else {
      this.contentWidth = bp.width;
      this._intendedWidth = null; // Clear intended width when manually selecting device
      this._autoZoom = false; // No zoom needed, device fits
    }

    this.requestUpdate();

    // Sync calculated zoom to DeviceManager after update
    requestAnimationFrame(() => {
      const calculatedZoom = this.calculateZoom();
      if (window["DeviceManager"]) {
        window["DeviceManager"].setScale(calculatedZoom);
      }
    });

    // Start continuous rendering during transition
    this.startTransitionAnimation();
  }

  /**
   * Set the zoom level programmatically
   * @param {number} zoomLevel - Zoom level (0.1 to 2, where 1 = 100%)
   * @param {boolean} skipSync - If true, don't sync back to DeviceManager (prevents feedback loop)
   */
  setZoom(zoomLevel, skipSync = false) {
    // Validate zoom level
    if (typeof zoomLevel !== "number" || zoomLevel <= 0 || zoomLevel > 5) {
      console.warn(`Invalid zoom level: ${zoomLevel}. Must be between 0.1 and 5`);
      return;
    }

    // Clear auto-zoom and set manual zoom
    this._autoZoom = false;
    this._manualZoom = zoomLevel;

    // Update DeviceManager with new scale (unless we're responding to DeviceManager)
    if (!skipSync && window["DeviceManager"]) {
      window["DeviceManager"].setScale(zoomLevel);
    }

    this.dispatchEvent(
      new CustomEvent("zoom-change", {
        detail: { zoom: zoomLevel, manual: true },
      }),
    );

    this.requestUpdate();
    // Start continuous rendering during transition
    this.startTransitionAnimation();
  }

  /**
   * Reset zoom to auto-calculated based on current device/content
   */
  resetZoom() {
    this._manualZoom = null;

    // If we have a device set, enable auto-zoom
    if (this.currentDevice) {
      this._autoZoom = true;
    }

    const newZoom = this.calculateZoom();

    // Update DeviceManager with calculated zoom
    if (window["DeviceManager"]) {
      window["DeviceManager"].setScale(newZoom);
    }

    this.dispatchEvent(
      new CustomEvent("zoom-change", {
        detail: { zoom: newZoom, manual: false },
      }),
    );

    this.requestUpdate();
    // Start continuous rendering during transition
    this.startTransitionAnimation();
  }

  /**
   * Get available device presets
   * @returns {Array} Array of device objects
   */
  getDevices() {
    return [...this.breakpoints];
  }

  /**
   * Get current zoom level
   * @returns {number} Current zoom level
   */
  getZoom() {
    return this.zoom;
  }

  /**
   * Check if current width matches any device and update currentDevice
   * @param {number} tolerance - Tolerance in pixels for matching (default: 5px)
   */
  matchDeviceByWidth(tolerance = 5) {
    const width = this._intendedWidth || this.contentWidth;

    // Find device that matches current width within tolerance
    const matchedDevice = this.breakpoints.find((bp) => Math.abs(bp.width - width) < tolerance);

    if (matchedDevice && (!this.currentDevice || this.currentDevice.width !== matchedDevice.width)) {
      this.currentDevice = matchedDevice;
      this.dispatchEvent(new CustomEvent("device-match", { detail: matchedDevice }));
      return true;
    } else if (!matchedDevice && this.currentDevice) {
      // Clear currentDevice if no match and we had a device set
      this.currentDevice = null;
      return false;
    }

    return !!matchedDevice;
  }

  /**
   * Get current device
   * @returns {Object|null} Current device object or null
   */
  getCurrentDevice() {
    return this.currentDevice;
  }

  /**
   * Get selected device name
   * @returns {string} Selected device label or empty string
   */
  get selectedDevice() {
    return this.currentDevice?.label || "";
  }

  /**
   * Set selected device by name
   * @param {string} deviceName - Device label to select
   */
  set selectedDevice(deviceName) {
    if (!deviceName) {
      this.currentDevice = null;
      this.requestUpdate();
      return;
    }
    this.setDevice(deviceName);
  }

  /**
   * Get selected device width
   * @returns {number} Selected device width or 0
   */
  get selectedDeviceWidth() {
    return this.currentDevice?.width || 0;
  }

  /**
   * Set selected device by width
   * @param {number} width - Device width to select
   */
  set selectedDeviceWidth(width) {
    if (!width || width <= 0) {
      this.currentDevice = null;
      this.requestUpdate();
      return;
    }
    const device = this.breakpoints.find((bp) => bp.width === width);
    if (device) {
      this.setDevice(device);
    } else {
      console.warn(`No device found with width ${width}`);
    }
  }

  render() {
    // Apply Stage Background from config/theme
    const stageBg = this.c.bgStage;
    const stage = this.shadowRoot?.querySelector(".rd-stage");
    const canvasWidth = stage?.getBoundingClientRect().width || 0;
    const canvasHeight = stage?.getBoundingClientRect().height || 0;

    // If contentWidth is at or near canvas width, make it full width
    const isFullWidth = this.contentWidth < 0 || (canvasWidth > 0 && Math.abs(this.contentWidth - canvasWidth) < 5);

    // When auto-zoom is enabled, use intended width for zoom calculations
    // Otherwise, use current content width (no scaling)
    const actualWidth = this._autoZoom && this._intendedWidth ? this._intendedWidth : this.contentWidth;

    // Calculate unscaled dimensions - when zoomed, container needs to be larger
    // so that after scaling it matches the wrapper's visual size
    const unscaledHeight = this.zoom !== 1 ? Math.round(canvasHeight / this.zoom) : canvasHeight;

    // For width: calculate the unscaled width based on what the wrapper will show
    let unscaledWidth;
    if (isFullWidth) {
      // Full width mode: account for the 20px padding
      unscaledWidth = this.zoom !== 1 ? Math.round((canvasWidth - 20) / this.zoom) : this.contentWidth - 20;
    } else {
      // Fixed width mode: scale the contentWidth
      unscaledWidth = this.zoom !== 1 ? Math.round(this.contentWidth / this.zoom) : this.contentWidth;
    }

    // Wrapper shows the visible/zoomed width
    const wrapperWidth = isFullWidth ? "calc(100% - 20px)" : this.contentWidth + "px";
    return html`
      <div class="rd-layout">
        <!-- Top Corner -->
        <div class="rd-corner"></div>

        <!-- Interaction Layers -->
        <div class="rd-ruler-top-container" id="interaction-top"></div>
        <div class="rd-ruler-left-container" id="interaction-left"></div>

        <!-- Stage Area -->
        <div class="rd-stage" style="background-color: ${stageBg}; ${isFullWidth ? "justify-content: start;" : ""}">
          <div class="rd-content-wrapper" style="width: ${wrapperWidth}; ">
            <div class="rd-zoom-container" style="--actual-width: ${unscaledWidth}px; --actual-height: ${unscaledHeight}px; --zoom-scale: ${this.zoom};">
              <slot @slotchange="${this.handleSlotChange}"></slot>
            </div>
            <div class="rd-resize-overlay"></div>
            <div class="rd-resize-handle" @mousedown="${this.startResize}"></div>
          </div>
        </div>
      </div>
      <canvas id="guides-canvas"></canvas>
    `;
  }
}

customElements.define("rd-editor-guides", RDEditorGuides);
